import React, { useEffect, useState } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

import { initTokenRefresh } from 'services';
import { useDispatch, useSelector } from 'store';
import { setAuth, selectIsAuth } from 'store/usersReducer';
import { getToken } from 'utils/token';
import Fingerprint from 'utils/fingerprint';
import Layout from 'pages/Layout';
import Companies from 'pages/Companies';
import Products from 'pages/Products';
import AdvertisingCampaignsLayout from 'pages/AdvertisingCampaigns';
import AdvertisingCampaignsList from 'pages/AdvertisingCampaigns/AdvertisingCampaignsList';
import AdvertisingCampaign from 'pages/AdvertisingCampaigns/AdvertisingCampaign';
import AdvertisingCampaignCreate from 'pages/AdvertisingCampaigns/AdvertisingCampaignCreate';
import AdvertisingCampaignPayment from 'pages/AdvertisingCampaigns/AdvertisingCampaignPayment';
import BalanceHistory from 'pages/BalanceHistory';
import Profile from 'pages/Profile';
import Login from 'pages/Auth/Login';
import Registration from 'pages/Auth/Registration';
import ForgotPassword from 'pages/Auth/ForgotPassword';
import ResetPassword from 'pages/Auth/ResetPassword';
import PrivacyPolicy from 'pages/PrivacyPolicy';
import Terms from 'pages/Terms';
import PublicOffer from 'pages/PublicOffer';
import classes from './App.module.scss';

const App: React.FC = () => {
	const dispatch = useDispatch();
	const [ isLoading, setIsLoading ] = useState(true);
	const isAuth = useSelector(selectIsAuth);

	const getAuthentication = async (): Promise<void> => {
		await Fingerprint.init();
		const token = getToken();
		if (token) {
			dispatch(setAuth(true));
			await initTokenRefresh();
		}
		setIsLoading(false);
	};

	useEffect(() => {
		getAuthentication();
	// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	if (isLoading) {
		return null;
	}

	return (
		<div className={classes.app}>
			<Routes>
				<Route path="/" element={<Layout />}>
					{isAuth 
						? (
							<>
								<Route path="companies" element={<Companies />} />
								<Route path=":companyId/products">
									<Route index element={<Products />} />
									<Route path=":productId" element={<AdvertisingCampaignsLayout />}>
										<Route index element={<AdvertisingCampaignsList />} />
										<Route path="create" element={<AdvertisingCampaignCreate />} />
										<Route path=":campaignId">
											<Route index element={<AdvertisingCampaign />} />
											<Route path="payment" element={<AdvertisingCampaignPayment />} />
										</Route>
									</Route>
								</Route>
								<Route path="balance/history" element={<BalanceHistory />} />
								<Route path="profile" element={<Profile />} />
							</>
						)
						: (
							<>
								<Route index element={<Navigate to="/login" replace />} />
								<Route path="/login" element={<Login />} />
								<Route path="/registration" element={<Registration />} />
								<Route path="/password-recovery" element={<ForgotPassword />} />
								<Route path="/reset-password/:resetToken" element={<ResetPassword />} />
							</>
						)
					}
					<Route path="/privacy-policy" element={<PrivacyPolicy />} />
					<Route path="/terms" element={<Terms />} />
					<Route path="/public-offer" element={<PublicOffer />} />
				</Route>
				<Route path="*" element={<Navigate to={isAuth ? '/' : '/login'} replace />} />
			</Routes>
			<ToastContainer
				className={classes.notification}
				position="top-center"
				autoClose={3000}
				newestOnTop={false}
				rtl={false}
				closeButton={false}
				hideProgressBar
				closeOnClick
				pauseOnFocusLoss
				draggable
				pauseOnHover
			/>
		</div>
	);
};

export default App;
